import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';

const AuthContext = createContext();

// Review-only mode (REACT_APP_PREVIEW=1 at build time): fake signed-in user, no Cognito. Never set in production builds.
const PREVIEW = process.env.REACT_APP_PREVIEW === '1';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      const newToken = session.tokens?.accessToken?.toString();
      if (newToken) setToken(newToken);
      return newToken;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    if (PREVIEW) {
      if (new URLSearchParams(window.location.search).get('login') === '1') { setUser(null); setLoading(false); return; }   // review: show the login screen
      setUser({ username: 'preview@amazon.com', signInDetails: { loginId: 'preview@amazon.com' } });
      setToken('preview');
      setLoading(false);
      return;
    }
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      
      setUser(currentUser);
      setToken(session.tokens?.accessToken?.toString());
    } catch (error) {
      setUser(null);
      setToken(null);
    }
    setLoading(false);
  };

  const login = async (userData) => {
    setUser(userData);
    try {
      const session = await fetchAuthSession();
      setToken(session.tokens?.accessToken?.toString());
    } catch (error) {
      console.error('Failed to get token after login:', error);
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setToken(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getAuthHeaders = () => {
    // Trigger background refresh if token might be stale
    refreshToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Auto-refresh token every 45 minutes
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(refreshToken, 45 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, refreshToken]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    getAuthHeaders
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
